// ============================================================
// INITIATIVE - rolling it, and the order it produces.
//
// Deathwatch RAW: Initiative is 1d10 + Agility Bonus, rolled once
// and kept for the fight. This module owns that sum, the two rulings
// a table can make about it, and the comparison the tracker sorts by.
//
// It deliberately does NOT know about TurnParticipant. The sort takes
// the three fields it actually reads (`InitiativeRanked`), so the
// tracker's shape can change without touching the ordering rules -
// and so this can be unit-tested on three-field literals.
//
// Pure. Zero React dependency. The only impurity is the die, which
// comes from `dice.ts` so the E2E harness can seed it.
// ============================================================
import { rollInitiativeDie } from './dice';
import {
  characteristicBonus,
  collectCharacterEffects,
  getEffectiveUnnaturalMultipliers,
} from './character/combatStatsResolver';
import type { Character } from './character/types';
import { getWSP } from './weaponSpecials';

// ── What a roll produced ─────────────────────────────────────

/**
 * The three parts of one initiative roll, kept separately.
 *
 * `total` is the only thing that sorts; `roll` and `bonus` exist so a
 * chip can explain itself ("1d10(7) + AB 4") and so a reroll can
 * replace the die without re-deriving the modifier from a stale sheet.
 */
export interface InitiativeRoll {
  roll: number;
  bonus: number;
  total: number;
}

/** The narrow view of a participant the ordering rules need. */
export interface InitiativeRanked {
  initiative: number | null;
  initiativeBonus: number | null;
  initiativeRoll: number | null;
}

// ── The rulings on offer ─────────────────────────────────────

export type InitiativeArrival = 'now' | 'slot' | 'next';
export type InitiativeTieBreak = 'agility' | 'roll' | 'order';

export const DEFAULT_INITIATIVE_ARRIVAL: InitiativeArrival = 'now';
export const DEFAULT_INITIATIVE_TIE_BREAK: InitiativeTieBreak = 'agility';

export interface InitiativeChoice<K extends string> {
  key: K;
  label: string;
  hint: string;
}

/**
 * When someone joins a round already in progress, do they act in it?
 *
 * `{key, label, hint}` like `UNNATURAL_DOS_MODES` and `COVER_CONFLICT_MODES`
 * - the settings registry imports these arrays rather than restating them,
 * so a new choice reaches the dialog by being added here.
 */
export const INITIATIVE_ARRIVALS: InitiativeChoice<InitiativeArrival>[] = [
  {
    key: 'now',
    label: 'Immediately, this round',
    hint: 'Reinforcements are dangerous the moment they land. Someone faster than whoever is acting takes their turn next, rather than waiting out the round.',
  },
  {
    key: 'slot',
    label: 'Only if their slot has not passed',
    hint: 'They act this round if their initiative is below the one acting - their place in the order is still ahead. Above it, their slot has gone and they start next round.',
  },
  {
    key: 'next',
    label: 'From the next round',
    hint: 'Every arrival waits, wherever they sort. A round stays one clean pass through the list.',
  },
];

/** Two participants roll the same number. What separates them? */
export const INITIATIVE_TIE_BREAKS: InitiativeChoice<InitiativeTieBreak>[] = [
  {
    key: 'agility',
    label: 'The higher Agility Bonus',
    hint: 'The quicker of the two goes first, as the core rules have it. Still tied, and they keep the order they joined in.',
  },
  {
    key: 'roll',
    label: 'The higher die',
    hint: 'The raw d10 decides, so a slow character who rolled a 10 beats a fast one who rolled a 3 to the same total.',
  },
  {
    key: 'order',
    label: 'Whoever joined first',
    hint: 'Nothing separates them but arrival. The most predictable - a tie never reshuffles on its own.',
  },
];

const ARRIVAL_KEYS = new Set<string>(INITIATIVE_ARRIVALS.map((c) => c.key));
const TIE_BREAK_KEYS = new Set<string>(INITIATIVE_TIE_BREAKS.map((c) => c.key));

/** A stored ruling is untrusted input - a later build may have written one this lacks. */
export function isInitiativeArrival(value: unknown): value is InitiativeArrival {
  return typeof value === 'string' && ARRIVAL_KEYS.has(value);
}

export function isInitiativeTieBreak(value: unknown): value is InitiativeTieBreak {
  return typeof value === 'string' && TIE_BREAK_KEYS.has(value);
}

// ── The bonus and the roll ───────────────────────────────────

/**
 * What gets added to the d10.
 *
 * Agility Bonus, multiplied by any Unnatural Agility - in Deathwatch an
 * unnatural characteristic multiplies the BONUS, and initiative is
 * denominated in bonuses, so it carries through. Then any flat
 * `initiative_modifier` from a talent or trait (Preternatural Speed's +2).
 *
 * A null character is a custom tracker entry - a name with no sheet
 * behind it - and gets a bare d10.
 */
export function initiativeBonus(character: Character | null | undefined): number {
  if (!character) return 0;

  const multiplier = getEffectiveUnnaturalMultipliers(character).agi ?? 1;
  let bonus = characteristicBonus(character.characteristics.agi.value) * multiplier;

  for (const effect of collectCharacterEffects(character)) {
    if (effect.type === 'initiative_modifier') bonus += effect.value;
  }

  bonus += quickeningOf(character);

  return bonus;
}

/**
 * Quickening(X) (v8.291.0): the swiftest weapon carried quickens the
 * hand. CARRIED = WIELDED (ADR): no per-weapon selection exists at
 * initiative time, so the best Quickening in the kit counts, once.
 */
function quickeningOf(character: Character): number {
  let quickening = 0;
  for (const w of character.weapons || []) {
    quickening = Math.max(quickening, getWSP(w.specialRules || [], 'Quickening'));
  }
  return quickening;
}

/** Roll 1d10 and add the bonus. The die comes from the initiative channel. */
export function rollInitiative(character?: Character | null, extra = 0): InitiativeRoll {
  const roll = rollInitiativeDie();
  // `extra` is what the table adds on top of the sheet - an aura's
  // `initiative` axis (v8.368.8). Part of the bonus, so the chip shows it.
  const bonus = initiativeBonus(character) + extra;
  return { roll, bonus, total: roll + bonus };
}

// ── Arrival ──────────────────────────────────────────────────

/**
 * The status a mid-round arrival is admitted with.
 *
 * This is the WHOLE of what the arrival ruling decides. The tracker's
 * scan looks for the first pending participant wherever it sits, so
 * nothing downstream has to branch on the mode: `now` makes them
 * pending and they get picked up on the next advance, `next` makes
 * them done and they do not.
 *
 * `activeTotal` is the initiative of whoever is acting, or null when
 * nobody is - in which case no slot can have passed.
 */
export function arrivalStatus(mode: InitiativeArrival, total: number, activeTotal: number | null): 'pending' | 'done' {
  if (mode === 'next') return 'done';
  if (mode === 'slot') {
    if (activeTotal === null) return 'pending';
    // The order descends, so a lower total sits LATER in the round and
    // its slot is still ahead of the pointer. A tie counts as passed.
    return total < activeTotal ? 'pending' : 'done';
  }
  return 'pending';
}

// ── Ordering ─────────────────────────────────────────────────

/**
 * Descending by initiative, then by the table's tie-break.
 *
 * Returns 0 rather than inventing a separator when the tie-break cannot
 * split them, so the caller's stable sort preserves arrival order -
 * that is what makes `'order'` a one-line case rather than a mode.
 */
export function compareInitiative(a: InitiativeRanked, b: InitiativeRanked, tieBreak: InitiativeTieBreak): number {
  // Nobody has rolled for them yet: to the back, but not past each other.
  if (a.initiative === null || b.initiative === null) return compareUnrolled(a.initiative, b.initiative);
  if (a.initiative !== b.initiative) return b.initiative - a.initiative;
  return TIE_BREAKERS.get(tieBreak)?.(a, b) ?? 0;
}

/** Unrolled (null) sorts after rolled; two unrolled keep their order. */
function compareUnrolled(a: number | null, b: number | null): number {
  if (a === b) return 0;
  return a === null ? 1 : -1;
}

/** How each tie-break splits an equal initiative, higher first; 'order' does not, leaving arrival order. */
const TIE_BREAKERS = new Map<string, (a: InitiativeRanked, b: InitiativeRanked) => number>([
  ['agility', (a, b) => (b.initiativeBonus ?? 0) - (a.initiativeBonus ?? 0)],
  ['roll', (a, b) => (b.initiativeRoll ?? 0) - (a.initiativeRoll ?? 0)],
]);

/** A new array in initiative order. Stable, so equal ranks keep arrival order. */
export function sortByInitiative<T extends InitiativeRanked>(items: readonly T[], tieBreak: InitiativeTieBreak): T[] {
  return [...items].sort((a, b) => compareInitiative(a, b, tieBreak));
}
